import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { useLanguage } from '../context/LanguageContext';
import { getLocalDevSubtasks, isLocalDevCompanyId, LOCAL_DEV_USERS } from '../utils/localDevAuth';
import { getApiErrorMessage, getAuthHeaders, getEffectiveCompanyId, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
const TeamPerformance = ({ user }) => {
  const { t } = useLanguage();
  const [teamMembers, setTeamMembers] = useState([]);
  const [appraisalData, setAppraisalData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = getEffectiveCompanyId(user);
  const formatRole = (role) => {
    if (role === 'team_member') return t('auth.teamMember');
    if (role === 'manager') return t('auth.manager');
    if (role === 'admin') return t('auth.admin');
    return role || t('common.na');
  };

  const getLocalTeamMembers = useCallback(() => (
    LOCAL_DEV_USERS
      .filter(localUser => localUser.company.id === user.company?.id && localUser.role === 'team_member')
      .map(localUser => ({
        id: localUser.id,
        user: localUser,
        manager: { id: user.id },
      }))
  ), [user]);

  const getLocalAppraisalData = useCallback((userId = '', from = '', to = '') => {
    const localTasks = getLocalDevSubtasks()
      .filter(task => task.major_task?.project?.company?.id === user.company?.id)
      .filter(task => !userId || String(task.assigned_to?.id) === String(userId))
      .filter(task => {
        const dueDate = String(task.due_date || task.due_datetime || '').slice(0, 10);
        if (from && dueDate < from) return false;
        if (to && dueDate > to) return false;
        return true;
      });

    return getLocalTeamMembers()
      .filter(member => !userId || String(member.user.id) === String(userId))
      .map(member => {
        const memberTasks = localTasks.filter(task => task.assigned_to?.id === member.user.id);
        const completedTasks = memberTasks.filter(task => task.status === 'completed').length;
        const totalTasks = memberTasks.length;
        return {
          user_id: member.user.id,
          name: member.user.email,
          code: member.user.email.split('@')[0],
          role: member.user.role,
          total_tasks: totalTasks,
          completed_tasks: completedTasks,
          ongoing_tasks: memberTasks.filter(task => task.status === 'ongoing').length,
          pending_approval_tasks: memberTasks.filter(task => task.status === 'pending_approval').length,
          overdue_tasks: memberTasks.filter(task => task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed').length,
          efficiency_score: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0,
        };
      });
  }, [getLocalTeamMembers, user]);

  const fetchData = useCallback(async () => {
    try {
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const localTeamMembers = getLocalTeamMembers();
        setTeamMembers(localTeamMembers);
        setAppraisalData(getLocalAppraisalData());
        setError('');
        return;
      }
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }

      const teamRes = await axios.get(`${API_URL}/api/team-members/`, {
        headers: getAuthHeaders(token),
      });

      const filteredTeamMembers = teamRes.data.filter(member => 
        member.manager?.id === user.id && member.user?.company?.id === user.company?.id
      );

      setTeamMembers(filteredTeamMembers);
      setError('');

      if (user.role === 'manager' && filteredTeamMembers.length > 0) {
        const appraisalPromises = filteredTeamMembers.map(tm =>
          axios.get(`${API_URL}/api/manager-appraisal-report/`, {
            headers: getAuthHeaders(token),
            params: { user_id: tm.user.id },
          }).catch(error => {
            if (error.response?.status === 404) {
              setError(t('teamPerformance.appraisalEndpointMissing'));
              return { data: [] };
            }
            if (error.response?.status === 403) {
              setError('You do not have permission or your company subscription is inactive.');
              return { data: [] };
            }
return { data: [] };
          })
        );
        const appraisalResponses = await Promise.all(appraisalPromises);
        setAppraisalData(appraisalResponses.flatMap(res => res.data));
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: t('toast.failedLoadData'),
      }));
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyId, getLocalAppraisalData, getLocalTeamMembers, isLocalDevSession, navigate, t, user]);

  useEffect(() => {
    if (user && user.id) {
      fetchData();
    } else {
      setLoading(false);
      setError(t('toast.userDataUnavailable'));
    }
  }, [fetchData, t, user]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('refresh') === 'true') {
      fetchData();
      navigate('/manager/team-assignments', { replace: true });
    }
  }, [fetchData, location.search, navigate]);

  const handleFilter = async () => {
    try {
      if (isLocalDevSession) {
        setAppraisalData(getLocalAppraisalData(selectedUser, startDate, endDate));
        setError('');
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      let url = `${API_URL}/api/manager-appraisal-report/`;
      const params = {};
      if (selectedUser) params.user_id = selectedUser;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const appraisalRes = await axios.get(url, {
        headers: getAuthHeaders(token),
        params,
      }).catch(error => {
        if (error.response?.status === 404) {
          setError(t('teamPerformance.appraisalEndpointMissing'));
          return { data: [] };
        }
        if (error.response?.status === 403) {
          setError('You do not have permission or your company subscription is inactive.');
          return { data: [] };
        }
        throw error;
      });

      setAppraisalData(appraisalRes.data);
      setError('');
    } catch (error) {
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: t('teamPerformance.failedFilter'),
      }));
    }
  };

  if (loading) {
    return <p className="text-text italic text-sm sm:text-base">{t('common.loading')}</p>;
  }

  return (
    <>
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
        {t('teamPerformance.title', { name: user.code || t('auth.manager') })}
      </h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      <div className="subtle-panel p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('teamPerformance.filterTitle')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div>
            <label htmlFor="team-member" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.assignee')}</label>
            <select
              id="team-member"
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">{t('common.allMembers')}</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.user.id}>{member.user.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="start-date" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.startDate')}</label>
            <input
              type="date"
              id="start-date"
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">{t('common.endDate')}</label>
            <input
              type="date"
              id="end-date"
              className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <button
          className="primary-btn px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base"
          onClick={handleFilter}
        >
          {t('common.applyFilters')}
        </button>
      </div>
      <div className="card p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('teamPerformance.cardsTitle')}</h2>
        {appraisalData.length === 0 ? (
          <p className="text-text italic text-sm sm:text-base">{t('teamPerformance.noData')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {appraisalData.map(member => (
              <div key={member.user_id} className="subtle-panel p-3 sm:p-4">
                <h3 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">{member.name} ({member.code})</h3>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.role')}:</strong> {formatRole(member.role)}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.totalTasks')}:</strong> {member.total_tasks}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.completed')}:</strong> {member.completed_tasks}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.ongoing')}:</strong> {member.ongoing_tasks}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.pendingApproval')}:</strong> {member.pending_approval_tasks}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.overdue')}:</strong> {member.overdue_tasks}</p>
                <p className="text-sm sm:text-base"><strong>{t('teamPerformance.efficiencyScore')}:</strong> {member.efficiency_score}%</p>
                <div className="mt-1 h-2.5 w-full max-w-xs rounded-full bg-purple-300/20 sm:mt-2 sm:max-w-sm">
                  <div
                    className="h-2.5 rounded-full bg-green-500"
                    style={{ width: `${member.efficiency_score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default TeamPerformance;
